import { useCallback, useState } from 'react';

/**
 * Password Input (piece 3): a field that hides a value the viewer already
 * knows. Paste is always allowed, no length cap sits below 64, the reveal
 * toggle starts off and is never remembered across fields, and the strength
 * meter appears on set and change, never on login.
 */
export type PasswordPurpose = 'login' | 'set' | 'change';

/** No cap below this: a lower one truncates a manager-generated secret. */
export const PASSWORD_LENGTH_CAP_FLOOR = 64;

export function autocompleteFor(purpose: PasswordPurpose): 'current-password' | 'new-password' {
  return purpose === 'login' ? 'current-password' : 'new-password';
}

export function showsStrengthMeter(purpose: PasswordPurpose): boolean {
  return purpose !== 'login';
}

/** The four steps the strength meter counts, written beside it so a person sees what the bar measures. */
export const PASSWORD_STRENGTH_STEPS = ['length8', 'length14', 'twoKinds', 'allKinds'] as const;
export type PasswordStrengthStep = (typeof PASSWORD_STRENGTH_STEPS)[number];

export function strengthSteps(password: string): { step: PasswordStrengthStep; met: boolean }[] {
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length;
  return [
    { step: 'length8', met: password.length >= 8 },
    { step: 'length14', met: password.length >= 14 },
    { step: 'twoKinds', met: password.length > 0 && classes >= 2 },
    { step: 'allKinds', met: classes >= 4 },
  ];
}

/** A coarse strength grade 0..4 for the native meter: the steps met. */
export function strengthOf(password: string): 0 | 1 | 2 | 3 | 4 {
  if (password.length === 0) return 0;
  return Math.min(4, strengthSteps(password).filter((s) => s.met).length) as 0 | 1 | 2 | 3 | 4;
}

export function assertLengthCap(maxLength: number | undefined): void {
  if (maxLength !== undefined && maxLength < PASSWORD_LENGTH_CAP_FLOOR) {
    throw new Error(`Password Input never caps below ${PASSWORD_LENGTH_CAP_FLOOR} characters (piece 3)`);
  }
}

/** The reveal toggle: off by default and local to one field. */
export function useReveal() {
  const [revealed, setRevealed] = useState(false);
  const toggle = useCallback(() => setRevealed((r) => !r), []);
  const reset = useCallback(() => setRevealed(false), []);
  return { revealed, toggle, reset };
}

export function usePasswordInput(purpose: PasswordPurpose, value: string) {
  const reveal = useReveal();
  const [capsLock, setCapsLock] = useState(false);
  const [focused, setFocused] = useState(false);
  return {
    ...reveal,
    autocomplete: autocompleteFor(purpose),
    showsMeter: showsStrengthMeter(purpose),
    strength: strengthOf(value),
    steps: strengthSteps(value),
    capsLock,
    setCapsLock,
    focused,
    setFocused,
    showsCapsLockWarning: focused && capsLock,
  };
}
